import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  IconButton,
  Box,
  Divider,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PsychologyIcon from "@mui/icons-material/Psychology";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SaveIcon from "@mui/icons-material/Save";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";
import BaseHeader from "../Common/BaseHeader";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { alpha } from "@mui/material/styles";
import DetailPLExcelUpload from "./DetailPLExcelUpload";
import PdfGenerator from "./PdfGenerator";
import FlipSignModeSettingsDialog from "./FlipSignModeSettingsDialog";
import { ClassificationEngineDialog } from "./ai-assistant/ClassificationEngine";
import { AIAssistantDialog } from "../Schedules/CA/FaAddition/AIAssistant";
import HiddenButton from "../utils/button/HiddenButton";
import { shouldUseCompactHeaderActions } from "../../utils/responsiveLayoutSettings";

const ACTION_MENU_ICON_COLORS = {
  ai: "primary",
  upload: "success",
  classify: "secondary",
  adjustments: "info",
  settings: "default",
};

const actionMenuItemSx = {
  borderRadius: 1,
  mx: 1,
  mb: 0.5,
  py: 1.5,
  "&:hover": {
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
  },
};

const ActionMenuItem = ({
  color = "default",
  description,
  icon,
  label,
  onClick,
}) => (
  <MenuItem onClick={onClick} sx={actionMenuItemSx}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
      <Box
        sx={{
          alignItems: "center",
          bgcolor: (theme) =>
            alpha(
              color === "default"
                ? theme.palette.text.primary
                : theme.palette[color].main,
              color === "default" ? 0.06 : 0.1
            ),
          borderRadius: 1,
          display: "flex",
          flexShrink: 0,
          height: 36,
          justifyContent: "center",
          width: 36,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant='body2' sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {description}
        </Typography>
      </Box>
    </Box>
  </MenuItem>
);

const Header = ({
  company,
  revenue,
  otherIncome,
  costOfSales,
  expenses,
  sectionTotals,
  flipSignMode,
  comments,
  reviewNote,
  currency,
  handleSave,
  saving = false,
  canEditDetailPL,
  onFlipSignModeChange,
  onExcelAddRows,
  onManageAdjustments,
  onClassifyApply,
  children,
}) => {
  const aiAssistantType =
    company.companyType === "investment" ? "details_invest_pl" : "details_pl";
  const [pdfAnchorEl, setPdfAnchorEl] = useState(null);
  const [toolsAnchorEl, setToolsAnchorEl] = useState(null);
  const pdfMenuOpen = Boolean(pdfAnchorEl);
  const toolsMenuOpen = Boolean(toolsAnchorEl);
  const handlePdfMenuOpen = (event) => setPdfAnchorEl(event.currentTarget);
  const handlePdfMenuClose = () => setPdfAnchorEl(null);
  const handleToolsMenuOpen = (event) => setToolsAnchorEl(event.currentTarget);
  const handleToolsMenuClose = () => setToolsAnchorEl(null);
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [classificationOpen, setClassificationOpen] = useState(false);
  const existingSectionCounts = useMemo(
    () => ({
      Revenue: revenue?.length || 0,
      "Other Income": otherIncome?.length || 0,
      "Cost of Sales": costOfSales?.length || 0,
      Expenses: expenses?.length || 0,
    }),
    [revenue, otherIncome, costOfSales, expenses]
  );
  const existingTaggedCount = useMemo(
    () =>
      [
        ...(revenue || []),
        ...(otherIncome || []),
        ...(costOfSales || []),
        ...(expenses || []),
      ].filter((row) => row?.schedule).length,
    [revenue, otherIncome, costOfSales, expenses]
  );
  const [useCompactActions, setUseCompactActions] = useState(() =>
    shouldUseCompactHeaderActions()
  );

  useEffect(() => {
    const updateActionMode = () => {
      setUseCompactActions(shouldUseCompactHeaderActions());
    };

    updateActionMode();
    window.addEventListener("resize", updateActionMode);
    window.visualViewport?.addEventListener("resize", updateActionMode);

    return () => {
      window.removeEventListener("resize", updateActionMode);
      window.visualViewport?.removeEventListener("resize", updateActionMode);
    };
  }, []);

  // Excel upload dialog state
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const handleOpenExcelUploadDialog = () => setExcelDialogOpen(true);
  const handleCloseExcelUploadDialog = () => setExcelDialogOpen(false);

  // Flip sign mode settings dialog state
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const handleOpenSettingsDialog = () => setSettingsDialogOpen(true);
  const handleCloseSettingsDialog = () => setSettingsDialogOpen(false);

  const handleSettingsSave = (newFlipSignMode) => {
    if (onFlipSignModeChange) {
      onFlipSignModeChange(newFlipSignMode);
    }
  };

  const handleToolsAction = (callback) => {
    handleToolsMenuClose();
    callback?.();
  };

  const iconButtonSx = {
    backgroundColor: "#F5F5F5",
    borderRadius: "8px",
    transition: "all 0.2s ease-in-out",
    "&:hover": { backgroundColor: "#E0E0E0" },
  };

  const menuPaperSx = {
    borderRadius: "12px",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.1)",
    minWidth: 240,
  };

  const actions = (
    <>
      {canEditDetailPL && (
        <Button
          variant='contained'
          color='success'
          type='button'
          onClick={() => handleSave()}
          disabled={saving}
          startIcon={<SaveIcon />}
          sx={{ textTransform: "none", whiteSpace: "nowrap" }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      )}
      {useCompactActions ? (
        <>
          <Tooltip title='Statement tools'>
            <IconButton onClick={handleToolsMenuOpen} sx={iconButtonSx}>
              <MiscellaneousServicesIcon sx={{ color: "#424242" }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={toolsAnchorEl}
            open={toolsMenuOpen}
            onClose={handleToolsMenuClose}
            sx={{
              mt: 1,
              "& .MuiPaper-root": menuPaperSx,
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.1,
                borderBottom: 1,
                borderColor: "divider",
                mb: 1,
              }}
            >
              <Typography
                variant='subtitle2'
                sx={{
                  fontWeight: 600,
                  color: "primary.main",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontSize: "0.75rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                Statement Tools
              </Typography>
            </Box>
            <ActionMenuItem
              color={ACTION_MENU_ICON_COLORS.ai}
              description='Open the guided assistant'
              icon={<PsychologyIcon color='primary' fontSize='small' />}
              label='AI Assistant'
              onClick={() => handleToolsAction(() => setAiAssistantOpen(true))}
            />
            {canEditDetailPL && (
              <>
                <Divider sx={{ my: 1 }} />
                <ActionMenuItem
                  color={ACTION_MENU_ICON_COLORS.upload}
                  description='Import detailed P&L rows'
                  icon={<UploadFileIcon color='success' fontSize='small' />}
                  label='Upload Excel'
                  onClick={() => handleToolsAction(handleOpenExcelUploadDialog)}
                />
                <ActionMenuItem
                  color={ACTION_MENU_ICON_COLORS.classify}
                  description='Classify rows from a document'
                  icon={<AutoAwesomeIcon color='secondary' fontSize='small' />}
                  label='AI Auto-Classify'
                  onClick={() =>
                    handleToolsAction(() => setClassificationOpen(true))
                  }
                />
                <ActionMenuItem
                  color={ACTION_MENU_ICON_COLORS.adjustments}
                  description='Maintain tax adjustment options'
                  icon={<TuneIcon color='info' fontSize='small' />}
                  label='Manage Adjustments'
                  onClick={() => handleToolsAction(onManageAdjustments)}
                />
                <ActionMenuItem
                  color={ACTION_MENU_ICON_COLORS.settings}
                  description='Adjust display sign settings'
                  icon={<SettingsIcon fontSize='small' color='primary.main' />}
                  label='Section Sign Mode'
                  onClick={() => handleToolsAction(handleOpenSettingsDialog)}
                />
              </>
            )}
          </Menu>
        </>
      ) : (
        <>
          <Button
            startIcon={<PsychologyIcon />}
            onClick={() => setAiAssistantOpen(true)}
            variant='outlined'
            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            AI Assistant
          </Button>
          {canEditDetailPL && (
            <>
              <Button
                startIcon={<UploadFileIcon />}
                onClick={handleOpenExcelUploadDialog}
                variant='outlined'
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                Upload
              </Button>
              <Button
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setClassificationOpen(true)}
                variant='outlined'
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                Auto-Classify
              </Button>
              <HiddenButton
                icon={<TuneIcon />}
                onClick={onManageAdjustments}
                label='Manage Adjustments'
                delay={0.2}
                expandedWidth={200}
              />
              <Tooltip title='Section Sign Mode Settings'>
                <IconButton
                  onClick={handleOpenSettingsDialog}
                  sx={iconButtonSx}
                >
                  <SettingsIcon color='primary' />
                </IconButton>
              </Tooltip>
            </>
          )}
        </>
      )}
      <Tooltip title='PDF options'>
        <IconButton onClick={handlePdfMenuOpen} sx={iconButtonSx}>
          <MoreVertIcon sx={{ color: "#424242" }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={pdfAnchorEl}
        open={pdfMenuOpen}
        onClose={handlePdfMenuClose}
        sx={{
          mt: 1,
          "& .MuiPaper-root": menuPaperSx,
        }}
      >
        <PdfGenerator
          company={company}
          revenue={revenue}
          otherIncome={otherIncome}
          costOfSales={costOfSales}
          expenses={expenses}
          sectionTotals={sectionTotals}
          currencyView={currency}
          notes={comments}
          reviewNotes={reviewNote}
          flipSignMode={flipSignMode}
        />
        {children}
      </Menu>
      <AIAssistantDialog
        open={aiAssistantOpen}
        onClose={() => setAiAssistantOpen(false)}
        type={aiAssistantType}
      />
      <ClassificationEngineDialog
        open={classificationOpen}
        onClose={() => setClassificationOpen(false)}
        companyType={company.companyType}
        currency={currency}
        existingSectionCounts={existingSectionCounts}
        existingTaggedCount={existingTaggedCount}
        onApply={onClassifyApply}
      />
    </>
  );

  return (
    <>
      <BaseHeader
        title='Detailed Income Statement'
        companyName={company.name}
        uen={company.uen}
        yearOfAssessment={company.yearOfAssessment}
        financialYearStart={company.financialYearStart}
        financialYearEnd={company.financialYearEnd}
        currency={currency}
        actions={actions}
        aiSettings={{
          canUseAi: false,
          renderAiType: "",
        }}
        companyType={company.companyType || "common"}
      />
      {canEditDetailPL && (
        <DetailPLExcelUpload
          company={company}
          open={excelDialogOpen}
          handleCloseDialog={handleCloseExcelUploadDialog}
          onExcelAddRows={onExcelAddRows}
        />
      )}
      <FlipSignModeSettingsDialog
        open={settingsDialogOpen}
        onClose={handleCloseSettingsDialog}
        currentFlipSignMode={flipSignMode}
        onSave={handleSettingsSave}
        sections={["Revenue", "Other Income", "Cost of Sales", "Expenses"]}
      />
    </>
  );
};

export default Header;
